import { sendSegmentIdentifyAction } from '@integration-frontends/common/analytics';
import { initLoaded } from '@integration-frontends/common/app';
import {
  clear,
  loginSuccess,
  loginThunk,
} from '@integration-frontends/common/auth/core/application';
import {
  AUTHENTICATE_SERVICE_TOKEN,
  IAuthenticateService,
} from '@integration-frontends/common/auth/core/model';
import { DI_CONTAINER, getObservabilityService } from '@integration-frontends/core';
import { push } from 'redux-first-history';
import { call, put, race, take } from 'typed-redux-saga';
import { environment } from '../../../../environments/environment';
import { initPayloadReceived } from '../../../actions';
import { IDENTITY_STORE_TOKEN, IIdentityStore } from '../../../common';
import { PanelEventType, sendPanelEvent, SmarPanelInitPayload } from '../../../common/smartsheet';
import { emitAuthMessage } from '../smar';
import { login } from './login';

let retryingAuth = false;

function smartsheetCanRetryAuth(payload: SmarPanelInitPayload) {
  return !retryingAuth && payload.accessToken != null;
}

function* smartsheetAuthRetry() {
  yield put(push('/loading'));

  // wait for another payload from SMAR
  sendPanelEvent(PanelEventType.AccessTokenExpired);
  const initPayload = yield take(initPayloadReceived);
  yield initPayloadAuth(initPayload);
}

function* waitForApiKeyLogin() {
  yield put(push('/getting-started'));
  return yield take(loginSuccess);
}

function* waitForInitPayloadLogin() {
  const initPayloadAction = yield take(initPayloadReceived);
  yield put(loginThunk(initPayloadAction.payload.initPayload?.accessToken) as never);
  return yield take(loginSuccess);
}

function* waitForLogin() {
  const identityStore: IIdentityStore = DI_CONTAINER.get(IDENTITY_STORE_TOKEN);
  const [apiSuccess, initPayloadSuccess] = yield race([
    call(waitForApiKeyLogin),
    call(waitForInitPayloadLogin),
  ]);
  const success = apiSuccess || initPayloadSuccess;
  if (success) {
    yield call(
      getObservabilityService().sendIdentity,
      success.payload.identity,
      environment?.environment,
    );
    yield put(sendSegmentIdentifyAction({ identity: success.payload.identity }));
    emitAuthMessage(success.payload.identity?.token);
  }
  yield call(identityStore.set, success.payload.identity);
}

export function* initPayloadAuth(
  action: ReturnType<typeof initPayloadReceived>,
  allowRetry = true,
) {
  const identityStore: IIdentityStore = DI_CONTAINER.get(IDENTITY_STORE_TOKEN);
  const authService: IAuthenticateService = DI_CONTAINER.get(AUTHENTICATE_SERVICE_TOKEN);
  const smarInitPayload = action.payload.initPayload;
  const token: string = smarInitPayload?.accessToken || null;

  // default to persisted identity (api key)
  let identity = yield call(identityStore.get);

  // attempt to login using the access token
  if (smarInitPayload.accessToken != null) {
    getObservabilityService().setGlobalContextProperty('AccessTokenProvided', true);
    const { success } = yield login(token);

    // use accessToken identity or api key identity that may have been persisted in the past
    identity = success ? success.payload.identity : identity;
  }

  const isAuthed = yield call(authService.isAuthenticated, identity?.token);

  if (!isAuthed) {
    if (allowRetry && smartsheetCanRetryAuth(smarInitPayload)) {
      retryingAuth = true;
      yield smartsheetAuthRetry();
    } else {
      yield put(initLoaded());
      yield put(clear());
      yield waitForLogin();
    }
  } else {
    yield call(getObservabilityService().sendIdentity, identity, environment?.environment);
    yield put(sendSegmentIdentifyAction({ identity }));
    emitAuthMessage(identity?.token);
  }

  retryingAuth = false;
  return true;
}
